import React, { useEffect, useMemo, useState } from 'react';
import './Header.css';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import { walletService } from '../../services/walletService';
import { useUser } from '../../context/UserContext';

function shorten(addr) {
  if (!addr) return '';
  return addr.slice(0, 4) + '...' + addr.slice(-4);
}

const CHAINS = {
  '0x1': { id: '0x1', name: 'Ethereum', rpcUrls: ['https://rpc.ankr.com/eth'], nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, blockExplorerUrls: ['https://etherscan.io/'] },
  '0x38': { id: '0x38', name: 'BSC', rpcUrls: ['https://bsc-dataseed.binance.org/'], nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 }, blockExplorerUrls: ['https://bscscan.com/'] },
  '0x2b6653dc': { id: '0x2b6653dc', name: 'TRON', rpcUrls: ['https://api.trongrid.io'], nativeCurrency: { name: 'TRON', symbol: 'TRX', decimals: 6 }, blockExplorerUrls: ['https://tronscan.org/'] },
};

const Header = () => {
  const { walletAddress, chainId, setWallet, loadUserData } = useUser();
  const [account, setAccount] = useState(walletAddress || '');
  const [currentChainId, setCurrentChainId] = useState(chainId || '');
  const [connecting, setConnecting] = useState(false);
  const [walletMenuOpen, setWalletMenuOpen] = useState(false);
  const [networkMenuOpen, setNetworkMenuOpen] = useState(false);
  
  // 断开钱包连接
  const handleDisconnect = () => {
    // 清除本地存储
    localStorage.removeItem('cw_account');
    localStorage.removeItem('cw_chain');
    localStorage.removeItem('token');
    
    // 清除状态
    setAccount('');
    setCurrentChainId('');
    setWallet('', '');
    
    // 关闭菜单
    setWalletMenuOpen(false);
    
    console.log('钱包已断开连接');
  };

  const provider = useMemo(() => {
    // Prioritize MetaMask, then OKX, BitKeep, Binance Chain Wallet
    if (typeof window !== 'undefined') {
      if (window.ethereum && window.ethereum.isMetaMask) return window.ethereum;
      if (window.okxwallet && window.okxwallet.isOKExWallet) return window.okxwallet;
      if (window.bitkeep && window.bitkeep.ethereum) return window.bitkeep.ethereum;
      if (window.BinanceChain) return window.BinanceChain;
      if (window.ethereum) return window.ethereum; // generic injected
    }
    return null;
  }, []);

  useEffect(() => {
    const saved = window.localStorage.getItem('cw_account');
    const savedChain = window.localStorage.getItem('cw_chain');
    if (saved) {
      setAccount(saved);
      setCurrentChainId(savedChain || '');
    }

    if (provider) {
      provider.request({ method: 'eth_chainId' }).then((id) => {
        setCurrentChainId(id);
        if (savedChain !== id) {
          window.localStorage.setItem('cw_chain', id);
        }
      }).catch(() => {});
      
      provider.on && provider.on('accountsChanged', (accs) => {
        const addr = accs && accs[0];
        if (addr) {
          setAccount(addr);
          window.localStorage.setItem('cw_account', addr);
        } else {
          setAccount('');
          window.localStorage.removeItem('cw_account');
          setWallet('', '');
        }
      });
      
      provider.on && provider.on('chainChanged', (id) => {
        setCurrentChainId(id);
        window.localStorage.setItem('cw_chain', id);
      });
    }
  }, [provider, setWallet]);

  // 钱包连接和签名验证
  const connectWithInjected = async () => {
    if (!provider) {
      alert('No injected wallet found. Please install MetaMask/OKX/BitKeep.');
      return;
    }
    try {
      setConnecting(true);
      
      // 1. 请求账户连接
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      const addr = accounts && accounts[0];
      if (!addr) {
        throw new Error('未获取到钱包地址');
      }

      // 2. 获取链ID
      const id = await provider.request({ method: 'eth_chainId' });
      
      // 3. 获取签名消息
      const message = await walletService.getSignMessage(addr);
      console.log('获取到的签名消息:', message);
      
      // 4. 请求用户签名
      const signature = await provider.request({
        method: 'personal_sign',
        params: [message, addr],
      });
      console.log('签名结果:', signature);

      // 4.5. 检查URL中是否有邀请码
      const urlParams = new URLSearchParams(window.location.search);
      const inviteCode = urlParams.get('ref') || urlParams.get('inviteCode');

      // 5. 提交签名到后端验证
      const result = await walletService.connectWallet(addr, id, message, signature, inviteCode);
      
      // 6. 更新状态
      setAccount(addr);
      setCurrentChainId(id);
      setWallet(addr, id);
      
      // 7. 连接成功后，立即加载用户数据以确保同步
      // 后端返回的result包含user对象，但为了确保数据最新，重新加载一次
      if (loadUserData) {
        // 延迟一点时间，确保后端数据已保存
        setTimeout(() => {
          loadUserData(addr);
        }, 300);
      }
      
      window.localStorage.setItem('cw_account', addr);
      window.localStorage.setItem('cw_chain', id);
      
      // 如果有邀请码，连接成功后清除URL参数
      if (inviteCode) {
        const url = new URL(window.location.href);
        url.searchParams.delete('ref');
        url.searchParams.delete('inviteCode');
        window.history.replaceState({}, '', url);
      }
      
      console.log('钱包连接成功:', result);
    } catch (e) {
      console.error('钱包连接失败:', e);
      if (e.code === 4001) {
        alert('用户拒绝了签名请求');
      } else {
        alert('钱包连接失败: ' + (e.message || '未知错误'));
      }
    } finally {
      setConnecting(false);
      setWalletMenuOpen(false);
    }
  };

  const ensureChain = async (targetId) => {
    if (!provider) return;
    try {
      await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: targetId }] });
      setCurrentChainId(targetId);
      window.localStorage.setItem('cw_chain', targetId);
    } catch (switchError) {
      // If unrecognized chain, try to add first
      if (switchError && switchError.code === 4902) {
        const cfg = CHAINS[targetId];
        if (!cfg) return;
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [{ chainId: cfg.id, chainName: cfg.name, rpcUrls: cfg.rpcUrls, nativeCurrency: cfg.nativeCurrency, blockExplorerUrls: cfg.blockExplorerUrls }],
          });
          setCurrentChainId(targetId);
          window.localStorage.setItem('cw_chain', targetId);
        } catch (addErr) {
          console.error(addErr);
        }
      } else {
        console.error(switchError);
      }
    } finally {
      setNetworkMenuOpen(false);
    }
  };

  const currentNetworkName = CHAINS[currentChainId]?.name || (currentChainId ? currentChainId : 'Unknown');

  return (
    <header className="header">
      <div className="header-left">
        <div className="logo">
          <div className="logo-icon"></div>
        </div>
        <span className="app-title">Connect Wallet v0.1.1</span>
      </div>
      <div className="header-right">
        <div className="network-group">
          <button className="network-btn" onClick={() => setNetworkMenuOpen((v) => !v)}>
            {currentNetworkName}
          </button>
          {networkMenuOpen && (
            <div className="dropdown">
              <button className="dropdown-item" onClick={() => ensureChain('0x1')}>Ethereum</button>
              <button className="dropdown-item" onClick={() => ensureChain('0x38')}>BSC</button>
            </div>
          )}
        </div>

        <div className="wallet-group">
          <button className="wallet-btn" onClick={() => (account ? setWalletMenuOpen((v) => !v) : connectWithInjected())} disabled={connecting}>
            {account ? shorten(account) : (connecting ? 'Connecting...' : 'Connect Wallet')}
          </button>
          {walletMenuOpen && account && (
            <div className="dropdown">
              <div className="dropdown-item-info">
                <div className="dropdown-item-label">钱包地址</div>
                <div className="dropdown-item-value">{account}</div>
              </div>
              <button className="dropdown-item danger" onClick={handleDisconnect}>断开连接</button>
            </div>
          )}
          {walletMenuOpen && !account && (
            <div className="dropdown">
              <button className="dropdown-item" onClick={connectWithInjected}>Injected (MetaMask/OKX/BitKeep)</button>
            </div>
          )}
        </div>

        <LanguageSelector />
        <div className="notification-icon">
          <span className="bell-icon">🔔</span>
        </div>
      </div>
    </header>
  );
};

export default Header;
